import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { track } from '../lib/tracking';

const CartContext = createContext();
const API_URL = process.env.REACT_APP_BACKEND_URL;

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState([]);
  const { user } = useAuth();

  useEffect(() => {
    if (user && user !== false) {
      loadCart();
    } else {
      try {
        const localCart = localStorage.getItem('diosa_cart');
        if (localCart) setCart(JSON.parse(localCart));
      } catch { localStorage.removeItem('diosa_cart'); }
    }
  }, [user]);

  const loadCart = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/cart`, { withCredentials: true });
      setCart(data.items || []);
    } catch { /* non-critical: keep existing cart state */ }
  };

  const saveCart = async (items) => {
    if (user && user !== false) {
      try {
        await axios.post(`${API_URL}/api/cart`, { items }, { withCredentials: true });
      } catch { /* non-critical: server sync will retry on next mutation */ }
    } else {
      localStorage.setItem('diosa_cart', JSON.stringify(items));
    }
  };

  const addToCart = (product, quantity, size, color) => {
    const idx = cart.findIndex(i => i.product_id === product.id && i.size === size && i.color === color);
    let newCart;
    if (idx >= 0) {
      newCart = [...cart];
      newCart[idx].quantity += quantity;
    } else {
      newCart = [...cart, { product_id: product.id, quantity, size, color, product }];
    }
    setCart(newCart);
    saveCart(newCart);
    track('add_to_cart', {
      product_id: product.id, product_name: product.name, quantity,
      value: (product.price || 0) * quantity, currency: 'INR',
      items: [{ product_id: product.id, name: product.name, price: product.price, quantity }],
    });
  };

  const removeFromCart = (product_id, size, color) => {
    const line = cart.find(i => i.product_id === product_id && i.size === size && i.color === color);
    const newCart = cart.filter(i => !(i.product_id === product_id && i.size === size && i.color === color));
    setCart(newCart);
    saveCart(newCart);
    track('remove_from_cart', {
      product_id, product_name: line?.product?.name,
      quantity: line?.quantity, value: (line?.product?.price || 0) * (line?.quantity || 1),
    });
  };

  const updateQuantity = (product_id, size, color, quantity) => {
    const newCart = cart.map(i =>
      (i.product_id === product_id && i.size === size && i.color === color)
        ? { ...i, quantity } : i
    );
    setCart(newCart);
    saveCart(newCart);
  };

  const clearCart = () => { setCart([]); saveCart([]); };

  const cartTotal = cart.reduce((sum, i) => sum + (i.product?.price || 0) * i.quantity, 0);
  const cartCount = cart.reduce((sum, i) => sum + i.quantity, 0);

  return (
    <CartContext.Provider value={{ cart, addToCart, removeFromCart, updateQuantity, clearCart, cartTotal, cartCount }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
